/**
 * TanStack Query hooks for the activity suggestion engine.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { apiFetch } from "@/lib/api";

export interface ActivityTemplate {
  id: string;
  slug: string;
  title: string;
  subtitle: string | null;
  description: string;
  min_age_months: number;
  max_age_months: number;
  duration_minutes: number | null;
  category: string;
  season: string[];
  indoor_outdoor: string;
  peak_moment: string;
  reminiscing_prompt: string;
  research_tags: string[];
  perspective_slugs: string[];
  pce_dimensions: string[];
  source: string;
}

export interface ActivitySuggestion {
  id: string;
  family_id: string;
  child_id: string | null;
  template_id: string;
  suggested_at: string;
  suggested_for: string | null;
  status: string;
  completed_at: string | null;
  rating: number | null;
  notes: string | null;
  personalized_description: string | null;
  personalized_peak_moment: string | null;
  photo_album_url: string | null;
  acted_by_name: string | null;
  template: ActivityTemplate;
}

const COMPLETED_KEY = ["activities", "completed"] as const;
const FAMILY_SUGGESTIONS_KEY = ["activities", "suggest", "family"] as const;
const childSuggestionsKey = (childId: string) =>
  ["activities", "suggest", "child", childId] as const;

export function useCompletedActivities() {
  return useQuery<ActivitySuggestion[], Error>({
    queryKey: COMPLETED_KEY,
    queryFn: () => apiFetch<ActivitySuggestion[]>("/api/activities/completed"),
    staleTime: 30_000,
  });
}

export function useFamilySuggestions() {
  return useQuery<ActivitySuggestion[], Error>({
    queryKey: FAMILY_SUGGESTIONS_KEY,
    queryFn: () => apiFetch<ActivitySuggestion[]>("/api/activities/suggest"),
    staleTime: 5 * 60_000,
  });
}

export function useChildSuggestions(childId: string | null | undefined) {
  return useQuery<ActivitySuggestion[], Error>({
    queryKey: childId ? childSuggestionsKey(childId) : ["activities", "suggest", "child", "none"],
    queryFn: () => {
      if (typeof childId !== "string") return Promise.resolve([]);
      return apiFetch<ActivitySuggestion[]>(
        `/api/children/${childId}/activities/suggest`,
      );
    },
    enabled: typeof childId === "string" && childId.length > 0,
    staleTime: 5 * 60_000,
  });
}

export function useRefreshFamilySuggestions() {
  const queryClient = useQueryClient();
  return useMutation<ActivitySuggestion[], Error, void>({
    mutationFn: () =>
      apiFetch<ActivitySuggestion[]>("/api/activities/refresh", {
        method: "POST",
      }),
    onSuccess: (data) => {
      queryClient.setQueryData(FAMILY_SUGGESTIONS_KEY, data);
    },
  });
}

interface UpdateInput {
  suggestionId: string;
  status?: "accepted" | "completed" | "skipped" | "snoozed" | undefined;
  rating?: number | undefined;
  notes?: string | undefined;
  photo_album_url?: string | undefined;
  suggested_for?: string | undefined;
}

export function useUpdateSuggestion() {
  const queryClient = useQueryClient();
  return useMutation<ActivitySuggestion, Error, UpdateInput>({
    mutationFn: ({ suggestionId, status, rating, notes, photo_album_url, suggested_for }) =>
      apiFetch<ActivitySuggestion>(`/api/activities/${suggestionId}`, {
        method: "PATCH",
        body: { status, rating, notes, photo_album_url, suggested_for },
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["activities"] });
    },
  });
}

export function usePersonalizeSuggestion() {
  const queryClient = useQueryClient();
  return useMutation<ActivitySuggestion, Error, { suggestionId: string }>({
    mutationFn: ({ suggestionId }) =>
      apiFetch<ActivitySuggestion>(
        `/api/activities/${suggestionId}/personalize`,
        { method: "POST" },
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["activities"] });
    },
  });
}
